import React, { Component } from 'react';
import { ASSET_MODE_PER_CELL, Entity } from '../shared/world';

function InlineChoice({ label, choices, value, onChange }) {
  return (
    <div className="props__row">
      <label className="props__label">{label}</label>
      <div>
        {choices.map((choice) => (
          <button key={choice} className={`props__inline-choice ${choice === value ? 'active' : ''}`} onClick={() => onChange(choice)}>
            {choice}
          </button>
        ))}
      </div>
    </div>
  );
}

function InlineChoiceProp({ prop, onChange }) {
  return <InlineChoice label={prop.label} choices={prop.choices} value={prop.value} onChange={onChange} />;
}

function IntProp({ prop, onChange }) {
  return (
    <div className="props__row">
      <label className="props__label">{prop.label}</label>
      <input
        className="props__input"
        type="number"
        min="0"
        max="5"
        value={prop.value}
        onChange={(e) => onChange(parseInt(e.currentTarget.value))}
      />
    </div>
  );
}

function AssetRow({ asset, assetId, assetLibrary, onChangeAssets }) {
  const otherAssets = assetLibrary.assets.filter((a) => a.kind === asset.kind && !a.name.includes('@'));
  return (
    <div className="props__row props__tight">
      <span className="props__asset-id">{assetId}</span>
      <select className="props__asset" value={asset.name} onChange={(e) => onChangeAssets(asset.name, e.currentTarget.value)}>
        {otherAssets.map((a) => (
          <option key={a.name}>{a.name}</option>
        ))}
      </select>
    </div>
  );
}

export default class LayerProperties extends Component {
  render() {
    const {
      world,
      assetLibrary,
      selection,
      activeLayer,
      onExtendEntity,
      onRemoveCells,
      onChangeAssets,
      onChangeDirections,
      onJoinEntities,
    } = this.props;
    if (!world) {
      return (
        <div className="props">
          <div className="props__body">
            <p className="props__empty">No selection</p>
          </div>
        </div>
      );
    }
    const selectedEntities = activeLayer.findEntitiesForCells(selection);
    const selectedEntityKinds = Array.from(selectedEntities).reduce((s, e) => s.add(e.kind), new Set());
    if (selectedEntities.size === 0) {
      // You selected only empty cells.
      return (
        <div className="props">
          <div className="props__body">
            <p className="props__empty">No selection</p>
          </div>
        </div>
      );
    } else if (selectedEntities.size === 1) {
      const entity = selectedEntities.values().next().value;
      // Find selected cells that are not part of this entity (yet).
      const otherCellIds = activeLayer.filterUnoccupiedCellIds(Array.from(selection));
      // Find assets in this entity.

      // Map selected cell IDs to asset IDs.
      const entityCellIds = Array.from(selection).filter((id) => entity.cellIds.includes(id));
      const entityCellIndices = entityCellIds.map((id) => entity.cellIds.indexOf(id));
      const selectedAssetIds = entityCellIndices.map((idx) => entity.assetIds[idx]);
      const uniqueAssetIds = Array.from(new Set(selectedAssetIds));
      const uniqueAssetNames = uniqueAssetIds.map((id) => world.assets[id]);
      const uniqueAssets = uniqueAssetNames.map((name) => assetLibrary.assets.find((a) => a.name === name));

      // Map selected cell IDs to directions.
      const selectedDirections = entityCellIndices.map((idx) => entity.directions[idx]);
      const uniqueDirections = Array.from(new Set(selectedDirections));
      const directionValue = uniqueDirections.length === 1 ? uniqueDirections[0] : null;

      return (
        <div className="props">
          <div className="props__header">
            <span className="entity__id">{entity.id}</span>
            <span className="entity__kind">{entity.kind}</span>
          </div>
          {/* Direction */}
          <div className="props__row props__tight">
            <span className="props__label-long">Direction for selected cells</span>
          </div>
          <InlineChoice
            label="Direction"
            key="direction"
            choices={['N', 'E', 'S', 'W']}
            value={directionValue}
            onChange={(value) => onChangeDirections(value)}
          />

          <div className="props__divider" />
          {/* Assets */}
          <div className="props__row props__tight">
            <span className="props__label-long">Assets for selected cells</span>
          </div>
          {uniqueAssets.map((asset, i) => (
            <AssetRow
              key={asset.name}
              assetId={uniqueAssetIds[i]}
              asset={asset}
              assetLibrary={assetLibrary}
              onChangeAssets={onChangeAssets}
            />
          ))}
          <div className="props__divider" />
          {this._renderProps(entity)}
          <div className="props__row">
            <div className="props__action">
              <button onClick={() => onRemoveCells(entity, selection)}>Remove Cells</button>
              <p className="props__help">Remove cells from the current entity.</p>
            </div>
          </div>
          {otherCellIds.length > 0 && (
            <div className="props__row">
              <div className="props__action">
                <button onClick={() => onExtendEntity(entity, otherCellIds)}>Extend Entity</button>
                <p className="props__help">Add the selected cells to the current entity.</p>
              </div>
            </div>
          )}
        </div>
      );
    } else if (selectedEntityKinds.size === 1) {
      const entityKind = Array.from(selectedEntityKinds)[0];
      const assetMode = Entity.assetModeForKind(entityKind);
      if (assetMode === ASSET_MODE_PER_CELL) {
        return (
          <div className="props">
            <div className="props__body">
              <div className="props__row">
                <div className="props__action">
                  <button onClick={() => onJoinEntities(Array.from(selectedEntities))}>Join {entityKind}s</button>
                  <p className="props__help">Join different entities into a single entity.</p>
                </div>
              </div>
            </div>
          </div>
        );
      } else {
        return (
          <div className="props">
            <div className="props__body">
              <p className="props__empty">Multiple selection</p>
            </div>
          </div>
        );
      }
    } else {
      return (
        <div className="props">
          <div className="props__body">
            <p className="props__empty">Multiple selection</p>
          </div>
        </div>
      );
    }
  }

  _renderProps(entity) {
    if (typeof entity.props !== 'object') return;
    const propNames = Object.keys(entity.props);
    const elements = [];
    for (const propName of propNames) {
      const prop = entity.props[propName];
      if (prop.type === 'int') {
        elements.push(<IntProp key={propName} prop={prop} onChange={(value) => this.props.onChangeEntityProp(entity, propName, value)} />);
      } else if (propName === 'direction') {
        elements.push(
          <InlineChoiceProp key={propName} prop={prop} onChange={(value) => this.props.onChangeEntityProp(entity, propName, value)} />
        );
      }
    }
    return elements;
  }
}
